import { addPagesFromContext, addWidgetsFromContext } from "@insite/client-framework/Configuration";
import { setPostStyleGuideTheme } from "@insite/client-framework/ThemeConfiguration";
import { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
import getColor from "@insite/mobius/utilities/getColor";
import hideAccessibilityWidgetOnPrint from "@tSurya/Common/Utilities/hideAccessibilityWidgetOnPrint";
import limitScrollRestorationToHistoryNavigation from "@tSurya/Common/Utilities/scrollRestoration";
import syncAccessibilityWidgetOffset from "@tSurya/Common/Utilities/syncAccessibilityWidgetOffset";
import "@tSurya/theme";
import { ELEMENTS, TYPOGRAPHY, Z_INDEX } from "@tSurya/theme/design";
import { css } from "styled-components";

limitScrollRestorationToHistoryNavigation();
hideAccessibilityWidgetOnPrint();
syncAccessibilityWidgetOffset();

// For production, include a set of commonly used widgets in the main bundle
// the rest will be split into their own chunks
// Do not do this for the ./Overrides folder
const widgets = IS_PRODUCTION
    ? require.context("./Widgets", true, /(Header|Basic|Common|Footer|SignIn)\/.+?\.tsx$/)
    : require.context("./Widgets", true, /\.tsx$/);
// enable hot module reloading for widgets
const onHotWidgetReplace = addWidgetsFromContext(widgets);
if (module.hot) {
    module.hot.accept(widgets.id.toString(), () => onHotWidgetReplace(require.context("./Widgets", true, /\.tsx$/)));
}

// Load all custom pages so they are included in the bundle and enable hot module reloading for them. Do not do this for the ./Overrides folder
// const pages = require.context("./Pages", true, /\.tsx$/);
// const onHotPageReplace = addPagesFromContext(pages);
// if (module.hot) {
//     module.hot.accept(pages.id, () => onHotPageReplace(require.context("./Pages", true, /\.tsx$/)));
// }
// const pages = require.context("./Pages", true, /\.tsx$/);
// const onHotPageReplace = addPagesFromContext(pages);
// if (module.hot) {
//     module.hot.accept(pages.id, () => onHotPageReplace(require.context("./Pages", true, /\.tsx$/)));
// }

// Load all handlers so they are included in the bundle.
const handlers = require.context("./Handlers", true);
handlers.keys().forEach(key => handlers(key));

const stylesExtensions = require.context("./StylesExtensions", true);
stylesExtensions.keys().forEach(key => stylesExtensions(key));

const store = require.context("./Store", true);
store.keys().forEach(key => store(key));

// in production, the widget extensions are included in the chunks created for widgets
// if (!IS_PRODUCTION) {
//     // load all widget extensions. They could be loaded individually instead
//     const widgetExtensions = require.context("./WidgetExtensions", true);
//     widgetExtensions.keys().forEach(key => widgetExtensions(key));
// }
// Mobius renders the select chevron as a fixed (sizeVariant height - 4)px box pinned 2px from the top.
// Brands that grow the form field have to shift it down by half the extra height to keep it centered.
const selectIconOffset = `calc((${(ELEMENTS as any).formField?.inputHeight} - ${
    sizeVariantValues.default.height - 4
}px) / 2)`;

const getButtonDisabledCss = () => {
    const disabled = (ELEMENTS.button.base as any)?.disabled;
    if (!disabled) return '';
    return `
        &:disabled {
            background: ${disabled.background} !important;
            border-color: ${disabled.borderColor} !important;
            color: ${disabled.color} !important;
            ${disabled.boxShadow ? `box-shadow: ${disabled.boxShadow};` : ''}
            cursor: not-allowed;
            opacity: 1;
            span[class^="TypographyStyle"] {
                color: ${disabled.color} !important;
            }
        }
    `;
};

setPostStyleGuideTheme({
    // specifies primary colors are not editable & fixed to these values
    typography: TYPOGRAPHY,
    focus: {
        color: "#09f",
        style: "solid",
        width: "0px",
    },
    dataTable:{
        defaultProps: {
            cssOverrides: {
                table: css`
                    position: relative;
                `,
            },
        },
    },
    button: {
        primary: {
            ...((ELEMENTS.button.base as any)?.hover ? {
                mergeCss: true,
                hoverMode: "none" as any,
                typographyProps: {
                    size: TYPOGRAPHY.button.size,
                    weight: TYPOGRAPHY.button.weight,
                    lineHeight: TYPOGRAPHY.button.lineHeight,
                    fontFamily: TYPOGRAPHY.button.fontFamily,
                },
            } : {}),
            css: css`
                ${!(ELEMENTS.button.base as any)?.hover ? 'height: 40px;' : ''}
                ${(TYPOGRAPHY.button as any).letterSpacing && `letter-spacing: ${(TYPOGRAPHY.button as any).letterSpacing};`}
                ${() => {
                    const hover = (ELEMENTS.button.base as any).hover;
                    if (!hover) return '';
                    return `
                        border: ${hover.borderWidth || '1px'} solid transparent;
                        &:hover {
                            background: ${hover.background};
                            border-color: ${hover.borderColor};
                            color: ${hover.color};
                            ${hover.boxShadow ? `box-shadow: ${hover.boxShadow};` : ''}
                            span[class^="TypographyStyle"] { color: ${hover.color}; }
                        }
                    `;
                }}
                ${getButtonDisabledCss()}
            `,
        },
        secondary: {
            ...((ELEMENTS.button as any)?.secondary ? {
                mergeCss: true,
                hoverMode: (ELEMENTS.button as any)?.secondary?.hover ? ("none" as any) : undefined,
                typographyProps: {
                    size: TYPOGRAPHY.button.size,
                    weight: TYPOGRAPHY.button.weight,
                    lineHeight: TYPOGRAPHY.button.lineHeight,
                    fontFamily: TYPOGRAPHY.button.fontFamily,
                },
            } : {}),
            css: css`
                ${!(ELEMENTS.button as any)?.secondary ? 'height: 40px;' : ''}
                ${(TYPOGRAPHY.button as any).letterSpacing && `letter-spacing: ${(TYPOGRAPHY.button as any).letterSpacing};`}
                ${() => {
                    const sec = (ELEMENTS.button as any).secondary;
                    if (!sec) return '';
                    let result = '';
                    if (sec.color) {
                        result += `color: ${sec.color};`;
                    }
                    if (sec.background) {
                        result += `
                            background: ${sec.background};
                            border: ${sec.borderWidth || '2px'} solid ${sec.borderColor || 'currentColor'};
                            ${sec.boxShadow ? `box-shadow: ${sec.boxShadow};` : ''}
                        `;
                    }
                    if (sec.hover) {
                        result += `
                            &:hover {
                                ${sec.hover.opacity ? `opacity: ${sec.hover.opacity};` : ''}
                                ${sec.hover.background ? `background: ${sec.hover.background};` : ''}
                                ${sec.hover.borderColor ? `border-color: ${sec.hover.borderColor};` : ''}
                                ${sec.hover.color ? `color: ${sec.hover.color}; span[class^="TypographyStyle"] { color: ${sec.hover.color}; }` : ''}
                            }
                        `;
                    }
                    return result;
                }}
                ${getButtonDisabledCss()}
            `,
        },
    },
    ...((ELEMENTS as any).radio ? {
        radio: {
            defaultProps: {
                color: "primary",
                css: css`
                    input {
                        & + label {
                            &::before {
                                height: ${(ELEMENTS as any).radio.size};
                                width: ${(ELEMENTS as any).radio.size};
                                border: 1px solid ${(ELEMENTS as any).radio.borderColor};
                            }
                            margin-left: -15px;
                        }
                        &:checked:not(:disabled) + label::before {
                            border-color: ${(ELEMENTS as any).radio.borderColor};
                            background: transparent;
                        }
                        &:checked + label::after {
                            height: ${(ELEMENTS as any).radio.dotSize};
                            width: ${(ELEMENTS as any).radio.dotSize};
                            left: ${(ELEMENTS as any).radio.dotOffset};
                            top: calc(50% - ${parseInt((ELEMENTS as any).radio.dotSize) / 2}px);
                            background: ${(ELEMENTS as any).radio.checkedBackground};
                        }
                    }
                `,
            },
        },
    } : {}),
    ...((ELEMENTS as any).formField?.inputHeight ? {
        // Mobius resolves every form field's cssOverrides through theme.textField (FormField hardcodes
        // component: "textField"), so this block is the only hook for sizing selects as well as inputs.
        textField: {
            defaultProps: {
                border: "rectangle" as any,
                cssOverrides: {
                    formInputWrapper: css`
                        input,
                        select:not([class*="react-datetime-picker"]) {
                            height: ${(ELEMENTS as any).formField.inputHeight} !important;
                            padding: ${(ELEMENTS as any).formField.inputPadding};
                            border-color: ${(ELEMENTS as any).formField.borderColor};
                        }
                        /* The chevron is pinned against Mobius' 40px default height, so re-center it. */
                        select + span[class^="IconWrapper"] {
                            top: ${selectIconOffset};
                        }
                    `,
                    formField: css`
                        label[class^="TypographyStyle"] {
                            font-family: ${(ELEMENTS as any).formField.label.fontFamily};
                            font-weight: ${(ELEMENTS as any).formField.label.fontWeight};
                            font-size: ${(ELEMENTS as any).formField.label.fontSize};
                            line-height: ${(ELEMENTS as any).formField.label.lineHeight};
                            color: ${(ELEMENTS as any).formField.label.color};
                        }
                        input:not(:disabled),
                        select:not(:disabled),
                        textarea:not(:disabled) {
                            border-width: 1px !important;
                        }
                        textarea:focus {
                            padding: 10px !important;
                        }
                    `,
                },
                hintProps: {
                    size: (ELEMENTS as any).formField.hint.fontSize,
                    weight: (ELEMENTS as any).formField.hint.fontWeight,
                    color: (ELEMENTS as any).formField.hint.color,
                } as any,
                errorProps: {
                    size: (ELEMENTS as any).formField.error.fontSize,
                    weight: (ELEMENTS as any).formField.error.fontWeight,
                    color: (ELEMENTS as any).formField.error.color,
                } as any,
            },
        },
    } : {}),
    ...((ELEMENTS as any).modal?.closeButton?.transparent ? {
        modal: {
            defaultProps: {
                closeButtonProps: {
                    css: css`
                        background: transparent !important;
                        border: none !important;
                        outline: none !important;
                        box-shadow: none !important;
                        padding-right: 0 !important;

                        &:hover,
                        &:focus,
                        &:active,
                        &:focus-visible {
                            background: transparent !important;
                            background-color: transparent !important;
                            border: none !important;
                            outline: none !important;
                            box-shadow: none !important;
                        }

                        &::before,
                        &::after {
                            display: none !important;
                        }

                        svg,
                        svg * {
                            outline: none !important;
                            box-shadow: none !important;
                        }

                        * {
                            outline: none !important;
                            box-shadow: none !important;
                            border: none !important;
                        }
                    `,
                },
            },
        },
    } : {}),
    zIndex: Z_INDEX
});

// Flicker guard reveal: fade-in #react-app once React hydrates with correct brand styles.
// HeadEnd.tsx injects opacity:0 on non-Surya brands; we add .brand-revealed class (opacity:1
// with CSS transition) once React has rendered, then clean up the guard style element.
if (typeof window !== "undefined") {
    var guard = document.getElementById("brand-flicker-guard");
    if (guard) {
        var reactApp = document.getElementById("react-app");
        if (reactApp) {
            // Use MutationObserver to detect when React renders/hydrates content
            var observer = new MutationObserver(function () {
                observer.disconnect();
                requestAnimationFrame(function () {
                    var app = document.getElementById("react-app");
                    if (app) {
                        app.classList.add("brand-revealed");
                    }
                    var loader = document.getElementById("brand-loader");
                    if (loader) loader.remove();
                    if ((window as any).__brandRevealTimer) {
                        clearTimeout((window as any).__brandRevealTimer);
                    }
                    // Remove the guard style after transition completes
                    setTimeout(function () {
                        var g = document.getElementById("brand-flicker-guard");
                        if (g) g.remove();
                    }, 300);
                });
            });
            observer.observe(reactApp, { childList: true, subtree: true });
        }
    }
}